import { Button, Row, Col, message, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input } from 'antd';
import { createKanji } from 'requests/kanji';
import DatePicker from 'utils/TimePicker';
import TextArea from 'antd/lib/input/TextArea';

export default function AddKanji() {
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    useEffect(() => {
        return () => {
            sessionStorage.setItem('is_form_changed', 0);
        };
    }, []);

    const handleCreateKanji = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            for (const key in values) {
                if (typeof values[key] === 'string') values[key] = values[key].trim();
            }
            form.setFieldsValue(values);

            const data = {
                word: values.word,
                level: values.level,
                exampleSentence: values.exampleSentence,
                demonstrative: values.demonstrative,
                videoUrl: values.videoUrl,
                learn: values.learn,
                status: 1,
                conventions: [
                    ...values.kunConventions.filter((e) => e).map((e) => ({ type: 'kun', reading: e })),
                    ...values.onConventions.filter((e) => e).map((e) => ({ type: 'on', reading: e })),
                ],
            };

            if (values.videoStart) {
                const hours = values.videoStart.getHours();
                const minutes = values.videoStart.getMinutes();
                const seconds = values.videoStart.getSeconds();
                data.videoStart = hours * 3600 + minutes * 60 + seconds;
            }
            if (values.videoEnd) {
                const hours = values.videoEnd.getHours();
                const minutes = values.videoEnd.getMinutes();
                const seconds = values.videoEnd.getSeconds();
                data.videoEnd = hours * 3600 + minutes * 60 + seconds;
            }

            await createKanji(data);
            sessionStorage.setItem('is_form_changed', 0);
            navigate(`/materials?level=${data.level}&page=999999`);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    const handleConventionInput = (value, remove) => {
        if (!value) remove();
    };

    return (
        <div className="add-edit-page">
            <div className="add-edit-form">
                <h1 className="mt-32 mb-40">{t('add_kanji')}</h1>
                <div className="add-input">
                    <Form
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        layout="horizontal"
                        form={form}
                        initialValues={{ kunConventions: [], onConventions: [] }}
                        onValuesChange={() => sessionStorage.setItem('is_form_changed', 1)}
                    >
                        <Form.Item
                            name="word"
                            label={t('kanji')}
                            rules={[{ required: true, pattern: /\S+/, message: t('required_field') }]}
                        >
                            <Input placeholder={t('input_kanji')} />
                        </Form.Item>

                        <Form.Item
                            name="level"
                            label={t('level')}
                            rules={[{ required: true, message: t('required_field') }]}
                        >
                            <Select type="number" placeholder={t('input_level')}>
                                <Select.Option value={5}>N5</Select.Option>
                                <Select.Option value={4}>N4</Select.Option>
                                <Select.Option value={3}>N3</Select.Option>
                                <Select.Option value={2}>N2</Select.Option>
                                <Select.Option value={1}>N1</Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.List name="kunConventions">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, index) =>
                                        index === 0 ? (
                                            <Form.Item label={t('how_to_read_kanji_kun')} key={field.key}>
                                                <Form.Item {...field} noStyle>
                                                    <Input
                                                        placeholder={t('convention_kun')}
                                                        onChange={(e) =>
                                                            handleConventionInput(e.target.value, () =>
                                                                remove(field.name),
                                                            )
                                                        }
                                                    />
                                                </Form.Item>
                                                <Button
                                                    className="add-convention-btn"
                                                    type="dashed"
                                                    onClick={() => add()}
                                                >
                                                    {t('add_btn')}
                                                </Button>
                                            </Form.Item>
                                        ) : (
                                            <Row key={field.key} style={{ justifyContent: 'flex-start' }}>
                                                <Col sm={8} md={8} lg={8}></Col>
                                                <Form.Item style={{ justifyContent: 'flex-start' }}>
                                                    <Form.Item
                                                        {...field}
                                                        style={{ justifyContent: 'flex-start' }}
                                                        noStyle
                                                    >
                                                        <Input
                                                            placeholder={t('convention_kun')}
                                                            onChange={(e) =>
                                                                handleConventionInput(e.target.value, () =>
                                                                    remove(field.name),
                                                                )
                                                            }
                                                        />
                                                    </Form.Item>
                                                    <Button
                                                        className="add-convention-btn"
                                                        type="dashed"
                                                        onClick={() => add()}
                                                    >
                                                        {t('add_btn')}
                                                    </Button>
                                                </Form.Item>
                                            </Row>
                                        ),
                                    )}
                                    {fields.length === 0 && (
                                        <Form.Item label={t('how_to_read_kanji_kun')}>
                                            <Button type="dashed" onClick={() => add()}>
                                                {t('add_btn')}
                                            </Button>
                                        </Form.Item>
                                    )}
                                </>
                            )}
                        </Form.List>

                        <Form.List name="onConventions">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, index) =>
                                        index === 0 ? (
                                            <Form.Item
                                                label={t('how_to_read_kanji_on')}
                                                key={field.key}
                                                className="field-with-btn"
                                            >
                                                <Form.Item {...field} noStyle>
                                                    <Input
                                                        placeholder={t('convention_on')}
                                                        onChange={(e) =>
                                                            handleConventionInput(e.target.value, () =>
                                                                remove(field.name),
                                                            )
                                                        }
                                                    />
                                                </Form.Item>
                                                <Button className="add-convention-btn" onClick={() => add()}>
                                                    {t('add_btn')}
                                                </Button>
                                            </Form.Item>
                                        ) : (
                                            <Row key={field.key} style={{ justifyContent: 'flex-start' }}>
                                                <Col sm={8} md={8} lg={8}></Col>
                                                <Form.Item style={{ justifyContent: 'flex-start' }}>
                                                    <Form.Item
                                                        {...field}
                                                        style={{ justifyContent: 'flex-start' }}
                                                        noStyle
                                                    >
                                                        <Input
                                                            placeholder={t('convention_on')}
                                                            onChange={(e) =>
                                                                handleConventionInput(e.target.value, () =>
                                                                    remove(field.name),
                                                                )
                                                            }
                                                        />
                                                    </Form.Item>
                                                    <Button className="add-convention-btn" onClick={() => add()}>
                                                        {t('add_btn')}
                                                    </Button>
                                                </Form.Item>
                                            </Row>
                                        ),
                                    )}
                                    {fields.length === 0 && (
                                        <Form.Item label={t('how_to_read_kanji_on')}>
                                            <Button type="dashed" onClick={() => add()}>
                                                {t('add_btn')}
                                            </Button>
                                        </Form.Item>
                                    )}
                                </>
                            )}
                        </Form.List>

                        <Form.Item name="exampleSentence" label={t('example_sentence')}>
                            <TextArea placeholder={t('input_example_sentence')} />
                        </Form.Item>

                        <Form.Item
                            name="demonstrative"
                            label={t('demonstrative')}
                            help={t('demonstrative_help')}
                            rules={[
                                { required: true, message: t('required_field') },
                                {
                                    pattern: /^[ぁ-んァ-ン一-龥]*\*[ぁ-んァ-ン一-龥]+\*[ぁ-んァ-ン一-龥]*$/,
                                    message: t('invalid_format'),
                                },
                            ]}
                        >
                            <Input placeholder={t('input_demonstrative')} />
                        </Form.Item>

                        <Form.Item
                            name="learn"
                            label={t('learn')}
                            rules={[{ required: true, message: t('required_field') }]}
                        >
                            <Input placeholder={t('input_learn')} />
                        </Form.Item>

                        <Form.Item name="videoUrl" label={t('video_url')}>
                            <Input placeholder={t('input_video_url')} />
                        </Form.Item>

                        <Form.Item name="videoStart" label={t('video_start')}>
                            <DatePicker picker="time" placeholder={t('input_video_start')} />
                        </Form.Item>

                        <Form.Item name="videoEnd" label={t('video_end')}>
                            <DatePicker picker="time" placeholder={t('input_video_end')} />
                        </Form.Item>
                    </Form>
                </div>
            </div>

            <div className="add-edit-btn">
                <Button onClick={() => navigate(-1)} className="ant-btn cancel-btn">
                    {t('cancel')}
                </Button>
                <Button className="ant-btn save-btn" onClick={handleCreateKanji} loading={loading}>
                    {t('save')}
                </Button>
            </div>
        </div>
    );
}
